import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { LanguageProvider } from '@/i18n';
import { ThemeProvider } from '@/contexts/ThemeContext';
import LoginPage from '@/pages/LoginPage';
import LoginLocalPage from '@/pages/LoginLocalPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DashboardLayout from '@/layouts/DashboardLayout';
import TitularDashboard from '@/pages/TitularDashboard';
import OperadorDashboard from '@/pages/OperadorDashboard';
import DPODashboard from '@/pages/DPODashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import ConsentsPage from '@/pages/ConsentsPage';
import MarketingActionsPage from '@/pages/MarketingActionsPage';
import ImportExportPage from '@/pages/ImportExportPage';
import SettingsPage from '@/pages/SettingsPage';
import AdminUsersPage from '@/pages/AdminUsersPage';
import DataBreachesPage from '@/pages/DataBreachesPage';
import AdminBackupsPage from '@/pages/AdminBackupsPage';
import AdminAuditLogsPage from '@/pages/AdminAuditLogsPage';
import QRCodePage from '@/pages/QRCodePage';
import OTPValidationPage from '@/pages/OTPValidationPage';
import ExportDataPage from '@/pages/ExportDataPage';
import TitularesPage from '@/pages/TitularesPage';
import ValidateConsentPage from '@/pages/ValidateConsentPage';
import RTBFPage from '@/pages/RTBFPage';
import ExpiringConsentsPage from '@/pages/ExpiringConsentsPage';
import GroupsPage from '@/pages/GroupsPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import TenantsPage from '@/pages/TenantsPage';
import TenantDashboardPage from '@/pages/TenantDashboardPage';
import ROPAPage from '@/pages/ROPAPage';
import PermissionsPage from '@/pages/PermissionsPage';
import RoleManagementPage from '@/pages/RoleManagementPage';
import LoginLogsPage from '@/pages/LoginLogsPage';
import CompliancePage from '@/pages/CompliancePage';
import PreferenceCenterPage from '@/pages/PreferenceCenterPage';
import AutolinePage from '@/pages/AutolinePage';
import DPOTenantSelectorPage from '@/pages/DPOTenantSelectorPage';
import DPOGlobalDashboard from '@/pages/DPOGlobalDashboard';
import PlatformSettingsPage from '@/pages/PlatformSettingsPage';
import AuthCallback from '@/components/AuthCallback';
import ProtectedRoute from '@/components/ProtectedRoute';
import SessionManager from '@/components/SessionManager';
import '@/App.css';

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login-local" element={<LoginLocalPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/validate-consent" element={<ValidateConsentPage />} />
      <Route path="/preferences/:token" element={<PreferenceCenterPage />} />
      
      <Route element={<ProtectedRoute />}>
        <Route path="/dpo/tenants" element={<DPOTenantSelectorPage />} />
        <Route path="/dpo/global" element={<DPOGlobalDashboard />} />
        <Route element={<DashboardLayout />}>
          <Route path="/dashboard" element={<TitularDashboard />} />
          <Route path="/operador" element={<OperadorDashboard />} />
          <Route path="/dpo" element={<DPODashboard />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/consents" element={<ConsentsPage />} />
          <Route path="/qr-code" element={<QRCodePage />} />
          <Route path="/otp-validation" element={<OTPValidationPage />} />
          <Route path="/export-data" element={<ExportDataPage />} />
          <Route path="/titulares" element={<TitularesPage />} />
          <Route path="/rtbf" element={<RTBFPage />} />
          <Route path="/expiring-consents" element={<ExpiringConsentsPage />} />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/marketing-actions" element={<MarketingActionsPage />} />
          <Route path="/import-export" element={<ImportExportPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/data-breaches" element={<DataBreachesPage />} />
          <Route path="/admin/backups" element={<AdminBackupsPage />} />
          <Route path="/admin/audit-logs" element={<AdminAuditLogsPage />} />
          <Route path="/admin/login-logs" element={<LoginLogsPage />} />
          <Route path="/tenants" element={<TenantsPage />} />
          <Route path="/tenant-dashboard/:tenantId" element={<TenantDashboardPage />} />
          <Route path="/ropa" element={<ROPAPage />} />
          <Route path="/permissions" element={<PermissionsPage />} />
          <Route path="/role-management" element={<RoleManagementPage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/autoline" element={<AutolinePage />} />
          <Route path="/superadmin/platform" element={<PlatformSettingsPage />} />
        </Route>
      </Route>
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <div className="App">
          <BrowserRouter>
            <SessionManager>
              <AppRouter />
            </SessionManager>
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;